import { useId } from 'react';
import clsx from 'clsx';
import type { ReactNode } from 'react';

import MinusIcon from '@app/components/icons/minus';
import CheckIcon from '@app/components/icons/check';
import { useBulkSelectionContext } from '@app/contexts/bulk-selection';

import style from './checkbox.module.scss';
import { SelectedItemsLabel } from '../label/label';

type Props = {
  disabled?: boolean;
  label?: ReactNode;
  labelClassName?: string;
};

function getCheckboxState({
  isAllItemsSelected,
  isSomeItemsSelected,
}: {
  isAllItemsSelected: boolean;
  isSomeItemsSelected: boolean;
}) {
  if (isAllItemsSelected) {
    return true;
  }
  if (isSomeItemsSelected) {
    return 'indeterminate';
  }

  return false;
}

export const Checkbox = ({ disabled, label, labelClassName }: Props) => {
  const id = useId();

  const bulkSelection = useBulkSelectionContext();

  const count = Object.keys(bulkSelection.selectedItemIds).length;
  const checked = getCheckboxState({
    isAllItemsSelected: bulkSelection.isAllItemsSelected(),
    isSomeItemsSelected: bulkSelection.isSomeItemsSelected(),
  });

  let icon: ReactNode = '';
  if (checked) {
    if (checked === 'indeterminate') {
      icon = (
        <MinusIcon
          className={style.Icon}
          height="12px"
          width="12px"
        />
      );
    } else {
      icon = (
        <CheckIcon
          className={style.Icon}
          height="10px"
          width="10px"
        />
      );
    }
  }

  const containerClasses = clsx(style.Label, {
    [style.DisabledCursor]: disabled,
  });

  const checkboxClasses = clsx(style.Checkbox, {
    [style.Checked]: checked,
    [style.Disabled]: disabled,
  });

  return (
    <div className={style.CheckboxContainer}>
      <input
        className={style.HiddenCheckbox}
        type="checkbox"
        id={`checkbox-${id}`}
        disabled={disabled}
        onChange={bulkSelection.toggleSelectAllItems}
        checked={checked === true}
      />
      <label
        className={containerClasses}
        htmlFor={`checkbox-${id}`}
      >
        <div
          className={checkboxClasses}
          aria-disabled={disabled}
          aria-checked={checked === 'indeterminate' ? 'mixed' : checked}
          role="checkbox"
        >
          {icon}
        </div>
        <span className={clsx(style.LabelText, labelClassName)}>
          {label ?? <SelectedItemsLabel count={count} />}
        </span>
      </label>
    </div>
  );
};
